// THE MODEL TREE, ASKED OF THE SERVER (owner, 2026-10-20).
//
// The viewer pages built their Project Files tree from a legacy per-document
// JSON loop. A streaming export has no such files - SpaceX BA03 has ZERO
// entries in the project's paths and pathsmapping - so those trees were
// legitimately empty and no click could ever land on a row. back/routes/
// modelTree serves the tree from the commit instead; this is the client half.
import { url, tokenRef } from "api";

const authHeaders = () => {
  const h = { "Content-Type": "application/json" };
  try {
    const t = localStorage.getItem(tokenRef);
    if (t) h.Authorization = `Bearer ${t}`;
  } catch (e) { /* no storage, no token */ }
  return h;
};

async function ask(path, init) {
  const res = await fetch(`${url}/model-tree${path}`, { headers: authHeaders(), ...(init || {}) });
  if (!res.ok) {
    // THE STATUS IS PART OF THE ANSWER. 404 on /locate means "this element is
    // not in this commit", which the page says in words; a 403 means no
    // access. Collapsing them into null is how a page ends up showing
    // "nothing here" for four different reasons.
    const err = new Error(`model-tree ${path} -> ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export const treeRoots = (projectId, commit) =>
  ask(`/${projectId}/roots${commit ? `?commit=${encodeURIComponent(commit)}` : ""}`);

export const treeChildren = (projectId, { category, family, type, skip, limit, commit } = {}) => {
  const q = new URLSearchParams();
  if (category !== undefined) q.set("category", category);
  if (family !== undefined) q.set("family", family);
  if (type !== undefined) q.set("type", type);
  if (skip) q.set("skip", String(skip));
  if (limit) q.set("limit", String(limit));
  if (commit) q.set("commit", commit);
  return ask(`/${projectId}/children?${q.toString()}`);
};

/**
 * WHICH TILES HOLD A BRANCH'S GEOMETRY (back/routes/modelTree /tiles).
 *
 * The fullest tiles first, at most `limit` of them, with the true tileCount
 * and the count of elements this export never placed (`untiled`) beside them -
 * so a bounded answer cannot be mistaken for a complete one. See
 * helpers/lazyCategoryGeometry.
 */
export const treeTiles = (projectId, { category, family, type, limit, commit } = {}) => {
  const q = new URLSearchParams();
  if (category !== undefined) q.set("category", category);
  if (family !== undefined) q.set("family", family);
  if (type !== undefined) q.set("type", type);
  if (limit) q.set("limit", String(limit));
  if (commit) q.set("commit", commit);
  return ask(`/${projectId}/tiles?${q.toString()}`);
};

/**
 * WHICH TILES HOLD THESE PARTICULAR ROWS (back/routes/modelTree POST /tiles).
 *
 * THE RIGHT QUESTION once the tree is showing rows. Asking by scope answers
 * about the fullest tiles of the whole branch: Conduits on BA03 is 18,686
 * elements across 549 tiles, so the 32 fullest hold 3,760 of them and nothing
 * says those are the 500 on screen. This asks about the rows in front of the
 * reader, and names the ones the export never placed.
 *
 * A POST because 500 uniqueIds is 18 KB of query string. It reads only.
 */
export const treeTilesForUids = (projectId, uids, { limit, commit } = {}) =>
  ask(`/${projectId}/tiles${commit ? `?commit=${encodeURIComponent(commit)}` : ""}`, {
    method: "POST",
    body: JSON.stringify({ uids, limit }),
  });

export const treeLocate = (projectId, uniqueId, commit) =>
  ask(`/${projectId}/locate?uniqueId=${encodeURIComponent(uniqueId)}${commit ? `&commit=${encodeURIComponent(commit)}` : ""}`);

/**
 * The keys react-simple-tree-menu must be given to open a branch.
 *
 * THE SERVER SPEAKS IN PIPES, THE TREE IN SLASHES. /locate returns
 * ["cat:Conduits", "cat:Conduits|fam:EMT", ...] - each entry the FULL path of
 * one ancestor - while the tree identifies a node by its ancestors' own keys
 * joined with "/". Translating here, once, is the difference between a tree
 * that opens and one that silently does not: handing it keys it cannot match
 * is exactly how the old four-level builder failed.
 */
export function openKeysFromLocate(locate) {
  const chain = (locate && locate.expand) || [];
  if (!chain.length) return [];
  // The deepest entry carries every segment; the rest are its prefixes.
  const segments = String(chain[chain.length - 1]).split("|");
  return segments.map((_, i) => segments.slice(0, i + 1).join("/"));
}

/** The leaf's own key in that same slash space. */
/**
 * THE ROW'S DOM ID, which is not the same thing as its library key.
 *
 * TreeViewerItem renders id={item.id}; serverBranch builds the leaf with
 * id = leafKey. react-simple-tree-menu's `key` - the ancestors joined with
 * "/" - is what openNodes wants and what leafKeyFromLocate returns. Scrolling
 * used the key, so getElementById found nothing on every click (2026-10-21:
 * tries 21, rowInDom false). Two identifiers, two functions, named for what
 * they are for.
 */
export function leafRowIdFromLocate(locate) {
  return (locate && locate.leafKey) || "";
}

export function leafKeyFromLocate(locate) {
  if (!locate || !locate.leafKey) return "";
  const open = openKeysFromLocate(locate);
  return open.length ? `${open[open.length - 1]}/${locate.leafKey}` : locate.leafKey;
}

/**
 * ONE BRANCH OF THE SERVER TREE, in the shape react-simple-tree-menu wants.
 *
 * Shared by every page that draws a model tree (owner, 2026-10-20: "the webgl
 * app needs to have its viewer work on any tree structure ... anywhere we use
 * the model tree/element ref"). A copy per page is how four pages ended up
 * with four versions of the same four bugs.
 *
 * Node keys are the server's own segments, so the open keys the library
 * composes with "/" are exactly what openKeysFromLocate returns. Siblings are
 * included WITHOUT children: the person sees where the element sits among its
 * peers without us fetching 102 categories to show one row.
 *
 * `meshInfo` IS WHAT MAKES A ROW SELECTABLE - TreeViewerItem lights a row only
 * when it has meshInfo and the page's clickedChild holds an object with the
 * same UniqueId. A leaf carrying a bare uniqueId can never highlight, which is
 * how the first version of this expanded correctly and selected nothing.
 */
export function serverBranch(loc, fams, types, leaves) {
  const cat = (loc && loc.element && loc.element.category) || "";
  const fam = (loc && loc.element && loc.element.family) || "";
  const typ = (loc && loc.element && loc.element.typeName) || "";
  const leafNodes = ((leaves && leaves.nodes) || []).map((n) => ({
    id: n.key, key: n.key, label: n.label, treeVisible: true,
    uniqueId: n.uniqueId,
    meshInfo: { UniqueId: n.uniqueId, Name: n.label, $id: n.key },
    nodes: [],
  }));
  const shown = leafNodes.length;
  const total = (leaves && leaves.total) || shown;
  // THE SCOPE EACH NODE STANDS FOR, carried on the node rather than parsed
  // back out of its key. The library joins ancestor keys with "/", and a Revit
  // type is called things like "3/4" - so every key parser is wrong on the
  // first conduit it meets. helpers/lazyCategoryGeometry reads this.
  const typeNodes = ((types && types.nodes) || []).map((t) => {
    const isOurs = t.key.endsWith(`type:${typ}`);
    // THE TRUE TOTAL, SAID OUT LOUD: a page of 500 out of 12,000 must not
    // look like all of them.
    const label = isOurs && total > shown
      ? `${t.label} (showing ${shown} of ${total})`
      : `${t.label} (${t.count})`;
    const typeName = isOurs ? typ : t.label;
    return {
      id: `type:${typeName}`, key: `type:${typeName}`,
      label, treeVisible: true, nodes: isOurs ? leafNodes : [],
      scope: { category: cat, family: fam, type: typeName },
    };
  });
  const famNodes = ((fams && fams.nodes) || []).map((fm) => {
    const isOurs = fm.key.endsWith(`fam:${fam}`);
    const familyName = isOurs ? fam : fm.label;
    return {
      id: `fam:${familyName}`, key: `fam:${familyName}`,
      label: `${fm.label} (${fm.count})`, treeVisible: true,
      nodes: isOurs ? typeNodes : [],
      scope: { category: cat, family: familyName },
    };
  });
  return [[{
    id: `cat:${cat}`, key: `cat:${cat}`, label: cat || "(no category)", treeVisible: true, nodes: famNodes,
    scope: { category: cat },
  }]];
}
